import React, { useState, useEffect } from "react";
import {
  FaDownload,
  FaCheckCircle,
  FaSpinner,
  FaTimes,
  FaUpload,
  FaCopy,
  FaTag,
  FaCheck,
  FaPaperPlane,
  FaClock,
  FaCalendarAlt,
  FaRupeeSign,
  FaRedo,
  FaEye,
  FaChartLine,
  FaInbox
} from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import DescriptionViewer from '../../Common/DescriptionViewer';
import AdsSubNav from '../../Common/AdsSubNav';
import CountdownTimer from '../../Common/CountdownTimer';

const InfluenerAcceptedLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [plateforms, setPlatforms] = useState([]);

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [navigating, setNavigating] = useState(false);
  const [formData, setFormData] = useState({
    adId: "",
    screenshot: null,
    channelName: "",
    channelLink: "",
    platformName: "",
    adTiming: "",
  });
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const [showAdPreview, setShowAdPreview] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const filteredLeads = leads.filter(lead => !lead.reporterEntry?.isExpired);
  const totalPagesFiltered = Math.ceil(filteredLeads.length / itemsPerPage);

  const stats = {
    acceptedAds: filteredLeads.length,
  };

  // Messages
  const [copiedId, setCopiedId] = useState(null);
  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Fetch accepted advertisements for the reporter
  // Body scroll lock and modal state
  useEffect(() => {
    if (showAdPreview || showModal) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('modal-open');
    } else {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('modal-open');
    }
    return () => {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('modal-open');
    };
  }, [showAdPreview, showModal]);

  const fetchAcceptedLeads = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/get/accepted/ads`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const leadsData = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data.data)
          ? response.data.data
          : Array.isArray(response.data.advertisements)
            ? response.data.advertisements
            : [];

      // Sort leads by acceptedAt descending
      const sortedLeads = [...leadsData].sort((a, b) => {
        const dateA = new Date(a.reporterEntry?.acceptedAt || 0).getTime();
        const dateB = new Date(b.reporterEntry?.acceptedAt || 0).getTime();
        return dateB - dateA;
      });
      setLeads(sortedLeads);
      setError(null);
    } catch (err) {
      console.error("Error fetching accepted advertisements:", err);
      setError("Failed to load accepted advertisements. Please try again later.");
      toast.error("Failed to load accepted advertisements");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAcceptedLeads();
  }, []);

  useEffect(() => {
    const fetchAdPricing = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/get/ad/pricing`
        );
        setPlatforms(res.data.data.plateforms || []);
      } catch (err) {
        console.error("Error fetching ad pricing:", err);
      }
    };
    fetchAdPricing();
  }, []);

  useEffect(() => {
    if (showModal || navigating || showAdPreview) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
    return () => document.body.classList.remove("modal-open");
  }, [showModal, navigating, showAdPreview]);

  // Pagination functions
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPagesFiltered) {
      setCurrentPage(newPage);
    }
  };

  // Calculate current page data
  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredLeads.slice(startIndex, startIndex + itemsPerPage);
  };

  const viewAdvertisement = (ad) => {
    setSelectedAd(ad);
    setShowAdPreview(true);
  };

  const closeAdPreview = () => {
    setShowAdPreview(false);
    setSelectedAd(null);
  };

  const copyAdDetails = () => {
    if (selectedAd && selectedAd.mediaDescription) {
      navigator.clipboard.writeText(selectedAd.mediaDescription);
      toast.success("Description copied!");
    }
  };

  // Handle download of advertisement
  const handleDownload = async (ad) => {
    if (!ad) {
      toast.error("No advertisement data provided.");
      return;
    }

    try {
      const mediaType = ad.mediaType?.toLowerCase();
      const url = mediaType === "video" ? ad.videoUrl : ad.imageUrl;

      if (!url) {
        toast.error("Media URL is missing.");
        return;
      }

      const fileName = `advertisement_${ad._id}.${mediaType === "video" ? "mp4" : "jpg"}`;
      
      const isApp = navigator.userAgent.includes('iInsafApp');
      if (isApp) {
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Download starting in app...");
        return;
      }

      const response = await fetch(url, {
        mode: 'cors',
        credentials: 'omit',
        referrerPolicy: 'no-referrer'
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }

      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);

      toast.success("Advertisement downloaded successfully!");
    } catch (err) {
      console.error("Error downloading advertisement:", err);
      toast.error("Failed to download advertisement");
    }
  };

  const handleConfirmUploads = (leadId) => {
    setFormData((prev) => ({ ...prev, adId: leadId }));
    setShowModal(true);
  };

  const handleCloseModal = () => {
    if (isSubmitting) {
      toast.warning("Please wait, submission in progress...");
      return;
    }
    setShowModal(false);
    if (screenshotPreview) {
      URL.revokeObjectURL(screenshotPreview);
      setScreenshotPreview(null);
    }
    setFormData({
      adId: "",
      screenshot: null,
      channelName: "",
      channelLink: "",
      platformName: "",
      adTiming: "",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "adTiming") {
      let cleanValue = value.replace(/[^\d:]/g, "");
      const prevValue = formData.adTiming;
      const isDeleting = value.length < prevValue.length;

      if (value === "") {
        setFormData((prev) => ({ ...prev, [name]: "" }));
        return;
      }

      if (isDeleting && cleanValue.includes(":") && cleanValue.length <= 3) {
        cleanValue = cleanValue.replace(":", "");
      }
      if (!isDeleting && cleanValue.length === 2 && !cleanValue.includes(":")) {
        cleanValue = cleanValue + ":";
      }
      if (cleanValue.replace(":", "").length === 0) {
        cleanValue = "";
      }
      if (cleanValue.length > 5) {
        cleanValue = cleanValue.substring(0, 5);
      }

      const mmssPattern = /^([0-5]?\d):([0-5]?\d)$/;
      if (cleanValue.length === 5 && !mmssPattern.test(cleanValue)) {
        toast.error("Please enter time in MM:SS format (e.g., 01:30)");
      }

      setFormData((prev) => ({ ...prev, [name]: cleanValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size must be less than 5MB");
        return;
      }
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }
      setFormData((prev) => ({ ...prev, screenshot: file }));
      const previewUrl = URL.createObjectURL(file);
      setScreenshotPreview(previewUrl);
    }
  };

  const removeScreenshot = () => {
    if (screenshotPreview) {
      URL.revokeObjectURL(screenshotPreview);
      setScreenshotPreview(null);
    }
    setFormData((prev) => ({ ...prev, screenshot: null }));
    const fileInput = document.querySelector('input[name="screenshot"]');
    if (fileInput) fileInput.value = "";
  };

  const handleSubmitConfirmation = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!formData.screenshot || !formData.channelName || !formData.channelLink || !formData.platformName || !formData.adTiming || !formData.adId) {
      toast.error("Please fill all required fields");
      setIsSubmitting(false);
      return;
    }

    try {
      const submitData = new FormData();
      submitData.append("adId", formData.adId);
      submitData.append("screenshot", formData.screenshot);
      submitData.append("channelName", formData.channelName);
      submitData.append("videoLink", formData.channelLink);
      submitData.append("platform", formData.platformName);
      submitData.append("duration", formData.adTiming);

      toast.info("Submitting your proof...", { autoClose: 1000 });

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/reporter/submit/proof`,
        submitData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 200) {
        toast.success("Upload confirmation submitted successfully!");
      }

      setLeads((prevLeads) => prevLeads.filter((lead) => (lead._id || lead.id) !== formData.adId));
      handleCloseModal();
      setNavigating(true);
      setTimeout(() => {
        navigate("/influencer/advertisement/running");
      }, 1500);
    } catch (error) {
      console.error("Error submitting confirmation:", error);
      const errorMessage = error.response?.data?.message || "Failed to submit confirmation.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPlatformStyle = (platform) => {
    const p = platform?.toLowerCase() || "";
    if (p.includes("youtube")) return "bg-red-50 text-red-700 border-red-100";
    if (p.includes("facebook")) return "bg-blue-50 text-blue-700 border-blue-100";
    if (p.includes("instagram")) return "bg-pink-50 text-pink-700 border-pink-100";
    return "bg-gray-50 text-gray-700 border-gray-100";
  };

  return (
    <div className="p-2 sm:p-3 md:p-4 lg:p-6 bg-white rounded-lg shadow-md animate-fadeIn">
      {navigating && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 backdrop-blur-sm">
          <div className="flex flex-col items-center p-8 bg-white rounded-2xl shadow-2xl border border-blue-200">
            <FaSpinner className="text-4xl text-blue-600 animate-spin mb-4" />
            <span className="text-lg font-semibold text-blue-700 tracking-wide">Redirecting...</span>
            <span className="text-sm text-gray-500 mt-2">Moving to Running Advertisements</span>
          </div>
        </div>
      )}

      <div className="mb-4 sm:mb-5 space-y-3 sm:space-y-4">
        <div className="bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 rounded-xl sm:rounded-[1.5rem] shadow-lg p-2.5 sm:p-4 text-white">
          <div className="flex flex-row items-center justify-between gap-2 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => navigate(-1)}
                className="hidden sm:flex px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/20 text-white text-[10px] sm:text-xs font-black uppercase tracking-wider border border-white/20 transition-all whitespace-nowrap"
              >
                ← Back
              </button>
              <div className="p-1.5 sm:p-2 bg-white/20 rounded-lg backdrop-blur-sm shadow-inner flex-shrink-0">
                <FaCheckCircle className="text-[12px] sm:text-base text-white" />
              </div>
              <div className="flex flex-col justify-center">
                <h1 className="text-xs sm:text-lg font-black tracking-tight leading-none mb-0.5 sm:mb-1">Active Ads</h1>
                <p className="text-green-50 text-[8px] sm:text-[10px] font-bold uppercase tracking-widest leading-none">Accepted: {stats.acceptedAds}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <div className="hidden sm:flex flex-col items-end pr-3 border-r border-white/20">
                <div className="text-sm sm:text-base font-black leading-none">{stats.acceptedAds}</div>
                <div className="text-[8px] sm:text-[9px] text-green-50 uppercase tracking-widest font-bold">Active Ads</div>
              </div>
              <button
                className="flex items-center justify-center gap-1.5 bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg transition-colors backdrop-blur-sm text-[10px] sm:text-xs tracking-wider font-black uppercase shadow-sm"
                onClick={fetchAcceptedLeads}
                disabled={loading}
              >
                <FaRedo className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
                <span className="hidden sm:inline">Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <AdsSubNav type="paid" />

      {loading && (
        <div className="flex justify-center items-center py-12">
          <FaSpinner className="animate-spin text-blue-500 text-4xl" />
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
          <FaTimes /> {error}
        </div>
      )}

      {!loading && !error && filteredLeads.length > 0 && (
        <div className="mt-6">
          {/* Mobile View */}
          <div className="block md:hidden space-y-4">
            {getCurrentPageData().map((lead, index) => {
              const isRejected = lead.reporterEntry?.initialProofRejected;
              return (
                <div key={lead._id || index} className={`bg-white border rounded-[1.5rem] shadow-sm overflow-hidden ${isRejected ? 'border-red-200 ring-1 ring-red-100' : 'border-gray-100'}`}>
                  <div className="flex flex-row">
                    <div className="w-[120px] h-[150px] min-w-[120px] bg-slate-100 relative group overflow-hidden border-r">
                      {lead.mediaType === "video" ? (
                        <div className="w-full h-full relative">
                          <video src={lead.videoUrl} className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-white">
                          <img src={lead.imageUrl} alt="Ad" className="w-full h-full object-cover rounded-none" />
                        </div>
                      )}
                      <div className="absolute bottom-1 right-1">
                        <span className="bg-indigo-600 text-white text-[9px] font-black px-1.5 py-0.5 rounded-full shadow-lg">
                          ₹{lead.finalReporterPrice || 0}
                        </span>
                      </div>
                    </div>

                    <div className="flex-1 p-3 flex flex-col justify-between">
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-1.5 overflow-hidden">
                            <span className="bg-slate-50 text-slate-500 border border-slate-200 px-1.5 py-0.5 rounded-md text-[9px] font-black tracking-tight shrink-0">
                              #{(lead._id || "").substring(0, 6).toUpperCase()}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <button onClick={() => viewAdvertisement(lead)} className="p-1.5 text-indigo-500 bg-indigo-50 rounded-lg"><FaEye size={10} /></button>
                            <button onClick={() => handleCopy(lead._id, `m-${lead._id}`)} className="p-1.5 text-slate-400 bg-slate-50 rounded-lg">
                              {copiedId === `m-${lead._id}` ? <FaCheck size={10} className="text-green-500" /> : <FaCopy size={10} />}
                            </button>
                          </div>
                        </div>

                        <div className="flex justify-between items-center gap-2">
                          <div className="flex flex-wrap items-center gap-1.5 min-w-0">
                            <span className="text-[11px] font-black text-slate-900 truncate uppercase">{lead.adType}</span>
                            {(lead.platforms?.length > 0 || lead.plateforms?.length > 0 || lead.channelType?.length > 0) && (
                              <span className="bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-widest border border-blue-100 whitespace-nowrap">
                                <span className="text-slate-900 font-[1000]">Post Type:</span> {lead.platforms?.[0] || lead.plateforms?.[0] || lead.channelType?.[0]}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center shrink-0 gap-1 bg-purple-50 px-2 py-0.5 rounded-md border border-purple-100">
                            <FaChartLine className="text-purple-600 text-[9px]" />
                            <span className="text-[10px] font-black text-purple-700">{lead.baseView || 0}</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <FaClock className="text-emerald-500 text-[10px]" />
                          <div className="flex flex-col">
                            <span className="text-[8px] text-slate-400 font-bold uppercase leading-none">Accepted At</span>
                            <span className="text-[10px] font-bold text-slate-600">
                              {lead.reporterEntry?.acceptedAt ? new Date(lead.reporterEntry.acceptedAt).toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : "---"}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-2 mt-3 pt-2 border-t border-slate-50">
                        <button onClick={() => handleDownload(lead)} className="p-2 bg-slate-50 text-slate-600 rounded-xl border border-slate-200/50"><FaDownload className="text-xs" /></button>
                        <button onClick={() => handleConfirmUploads(lead._id)} className={`flex-1 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest text-white shadow-md transition-all ${isRejected ? 'bg-orange-500' : 'bg-indigo-600'}`}>
                          {isRejected ? "Resubmit" : "Submit Proof"}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="bg-blue-50/50 px-4 py-1.5 border-t border-blue-100 flex justify-between items-center">
                    <span className="text-[9px] font-black text-blue-600 uppercase tracking-widest">Time Remaining</span>
                    <span className="text-[10px]"><CountdownTimer targetDate={lead.reporterEntry?.expiresAt} expiredText="EXPIRED" /></span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop View */}
          <div className="hidden md:block overflow-hidden bg-white border border-gray-200 rounded-xl shadow-sm">
            <table className="min-w-full text-left text-xs">
              <thead className="bg-gray-50 border-b border-gray-200 text-gray-500 uppercase tracking-wider font-semibold">
                <tr>
                  <th className="px-4 py-3">Ad Media</th>
                  <th className="px-4 py-3">Ref ID</th>
                  <th className="px-4 py-3">Accepted At</th>
                  <th className="px-4 py-3 text-center">Required Views</th>
                  <th className="px-4 py-3">Reward</th>
                  <th className="px-4 py-3 text-center">Deadline</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {getCurrentPageData().map((lead) => {
                  const isRejected = lead.reporterEntry?.initialProofRejected;
                  return (
                    <tr key={lead._id} className={`hover:bg-gray-50/80 transition-colors ${isRejected ? 'bg-red-50/30' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-[120px] h-[150px] bg-gray-100 rounded-xl overflow-hidden border relative group flex-shrink-0 cursor-pointer shadow-sm" onClick={() => viewAdvertisement(lead)}>
                            {lead.mediaType === "video" ? (
                              <video src={lead.videoUrl} className="w-full h-full object-cover" />
                            ) : (
                              <img src={lead.imageUrl} className="w-full h-full object-cover" />
                            )}
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity rounded-xl">
                              <FaEye className="text-white text-xl" />
                            </div>
                          </div>
                          <div className="min-w-0">
                            <p className="font-black text-slate-800 text-[11px] truncate uppercase flex flex-wrap items-center gap-1.5">
                              {lead.adType}
                              {(lead.platforms?.length > 0 || lead.plateforms?.length > 0 || lead.channelType?.length > 0) && (
                                <span className="bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-widest border border-blue-100 whitespace-nowrap">
                                  <span className="text-slate-900 font-[1000]">Post Type:</span> {lead.platforms?.[0] || lead.plateforms?.[0] || lead.channelType?.[0]}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono font-bold text-slate-400">#{(lead._id || "").substring(0, 8).toUpperCase()}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col">
                          <span className="font-bold text-slate-700">{lead.reporterEntry?.acceptedAt ? new Date(lead.reporterEntry.acceptedAt).toLocaleDateString(undefined, { day: '2-digit', month: 'short' }) : "---"}</span>
                          <span className="text-[10px] text-slate-400 font-bold">{lead.reporterEntry?.acceptedAt ? new Date(lead.reporterEntry.acceptedAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : ""}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="inline-flex items-center gap-1.5 bg-purple-50 px-2.5 py-1 rounded-lg border border-purple-100">
                          <FaChartLine className="text-purple-600 text-[10px]" />
                          <span className="font-black text-purple-700">{lead.baseView || 0}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-black text-emerald-600 text-sm">₹{lead.finalReporterPrice || 0}</td>
                      <td className="px-4 py-3 text-center"><CountdownTimer targetDate={lead.reporterEntry?.expiresAt} expiredText="EXPIRED" /></td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <button onClick={() => viewAdvertisement(lead)} className="p-2 text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg border border-indigo-200"><FaEye size={12} /></button>
                          <button onClick={() => handleDownload(lead)} className="p-2 text-slate-600 bg-slate-50 hover:bg-slate-100 rounded-lg border border-slate-200"><FaDownload size={12} /></button>
                          <button onClick={() => handleConfirmUploads(lead._id)} className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest text-white shadow-sm transition-all ${isRejected ? 'bg-orange-500 hover:bg-orange-600' : 'bg-indigo-600 hover:bg-slate-900'}`}>
                            {isRejected ? "Resubmit" : "Submit Proof"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPagesFiltered > 1 && (
            <div className="mt-8 flex justify-center gap-2">
              {[...Array(totalPagesFiltered)].map((_, i) => (
                <button key={i} onClick={() => handlePageChange(i + 1)} className={`w-8 h-8 rounded-lg text-xs font-black transition-all ${currentPage === i + 1 ? 'bg-indigo-600 text-white shadow-lg' : 'bg-white text-slate-400 border border-slate-200'}`}>
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Detail Preview Modal */}
      {showAdPreview && selectedAd && (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center p-0 sm:p-4 bg-slate-900/90 backdrop-blur-xl overflow-y-auto outline-none focus:outline-none">
          <motion.div initial={{ opacity: 0, scale: 0.95, y: 30 }} animate={{ opacity: 1, scale: 1, y: 0 }} className="bg-white lg:rounded-[2.5rem] shadow-2xl w-full max-w-2xl min-h-screen sm:min-h-0 sm:h-auto overflow-hidden relative my-auto">
            <div className="bg-slate-900 px-6 py-6 flex justify-between items-center text-white sticky top-0 z-10">
              <div>
                <h2 className="text-xl font-[1000] tracking-tight">Lead Intelligence</h2>
                <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-1">Detailed Active Lead</p>
              </div>
              <button onClick={closeAdPreview} className="p-3 bg-white/10 hover:bg-white/20 rounded-2xl transition-all"><FaTimes size={20} /></button>
            </div>
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <div className="rounded-[2rem] overflow-hidden bg-slate-900 mb-8 shadow-inner">
                {selectedAd.mediaType?.toLowerCase() === "video" ? <video src={selectedAd.videoUrl} controls className="w-full max-h-[300px]" /> : <img src={selectedAd.imageUrl} className="w-full h-auto object-contain max-h-[400px]" alt="Ad" />}
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <div className="bg-indigo-50 p-4 rounded-3xl border border-indigo-100 flex flex-col items-center">
                  <FaChartLine className="text-indigo-600 mb-1" size={20} />
                  <span className="text-[10px] text-indigo-400 font-black uppercase">Required Views</span>
                  <span className="text-lg font-black text-indigo-800">{selectedAd.baseView || 0}</span>
                </div>
                <div className="bg-emerald-50 p-4 rounded-3xl border border-emerald-100 flex flex-col items-center">
                  <FaRupeeSign className="text-emerald-600 mb-1" size={20} />
                  <span className="text-[10px] text-emerald-400 font-black uppercase">Reward</span>
                  <span className="text-lg font-black text-emerald-800">₹{selectedAd.finalReporterPrice || 0}</span>
                </div>
                <div className="bg-purple-50 p-4 rounded-3xl border border-purple-100 flex flex-col items-center">
                  <FaClock className="text-purple-600 mb-1" size={20} />
                  <span className="text-[10px] text-purple-400 font-black uppercase">Length</span>
                  <span className="text-lg font-black text-purple-800">{selectedAd.adLength}s</span>
                </div>
                <div className="bg-amber-50 p-4 rounded-3xl border border-amber-100 flex flex-col items-center">
                  <FaCalendarAlt className="text-amber-600 mb-1" size={20} />
                  <span className="text-[10px] text-amber-400 font-black uppercase">Type</span>
                  <span className="text-sm font-black text-amber-800">{selectedAd.adType}</span>
                </div>
              </div>
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-slate-900 font-[1000] text-lg tracking-tight">Campaign Brief</h3>
                  <button onClick={copyAdDetails} className="flex items-center gap-2 text-indigo-600 font-black text-[10px] uppercase tracking-widest hover:text-indigo-800 transition-colors"><FaCopy /> Copy Details</button>
                </div>
                <div className="bg-slate-50 border border-slate-100 rounded-[2rem] p-6">
                  <DescriptionViewer description={selectedAd.mediaDescription || ""} textSize="text-base" />
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row gap-3 sticky bottom-0 z-10">
              <button onClick={() => handleDownload(selectedAd)} className="flex-1 bg-slate-900 text-white py-4 rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl hover:bg-slate-800 transition-all flex items-center justify-center gap-3"><FaDownload /> Download Media</button>
              <button onClick={() => { closeAdPreview(); handleConfirmUploads(selectedAd._id); }} className="flex-1 bg-indigo-600 text-white py-4 rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl hover:bg-indigo-700 transition-all flex items-center justify-center gap-3"><FaUpload /> Submit Proof Now</button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Proof Submission Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center p-0 sm:p-4 bg-slate-900/90 backdrop-blur-xl overflow-y-auto outline-none focus:outline-none">
          <motion.div initial={{ opacity: 0, scale: 0.95, y: 30 }} animate={{ opacity: 1, scale: 1, y: 0 }} className="bg-white lg:rounded-[2rem] w-full max-w-xl shadow-2xl relative my-auto min-h-screen sm:min-h-0 sm:h-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
              <h2 className="text-lg font-black text-slate-800 uppercase tracking-tight flex items-center gap-2 flex-wrap">
                Requirement Verification
                {(() => {
                  const lead = leads.find((l) => (l._id === formData.adId) || (l.id === formData.adId));
                  if (!lead) return null;
                  const platform = lead.platforms?.[0] || lead.plateforms?.[0] || lead.channelType?.[0];
                  if (!platform) return null;
                  return (
                    <span className="bg-blue-50 text-blue-600 px-2 py-1 rounded-md text-[10px] font-black uppercase tracking-widest border border-blue-100 inline-flex items-center">
                      <span className="text-slate-900 font-[1000] mr-1">Post Type:</span> {platform}
                    </span>
                  );
                })()}
              </h2>
              <button onClick={handleCloseModal} className="p-2 text-gray-400 hover:text-red-500 transition-colors"><FaTimes size={18} /></button>
            </div>
            <div className="p-6">
              <form onSubmit={handleSubmitConfirmation} className="space-y-4">
                <div className="space-y-2">
                  <label className="block text-xs font-black text-slate-500 uppercase tracking-widest">Initial Proof Screenshot *</label>
                  <div className="relative group">
                    <input type="file" name="screenshot" onChange={handleFileChange} className="hidden" id="screenshot-upload" accept="image/*" required />
                    {!screenshotPreview ? (
                      <label htmlFor="screenshot-upload" className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-slate-200 rounded-[1.5rem] bg-slate-50 cursor-pointer hover:bg-blue-50 hover:border-blue-400 transition-all group">
                        <FaUpload className="text-slate-300 text-3xl mb-2 group-hover:scale-110 transition-transform" />
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Take Screenshot & Upload</span>
                        <span className="text-[10px] text-slate-400 mt-1">PNG, JPG up to 5MB</span>
                      </label>
                    ) : (
                      <div className="relative rounded-[1.5rem] overflow-hidden border border-gray-200">
                        <img src={screenshotPreview} alt="Preview" className="w-full h-40 object-contain bg-slate-50" />
                        <button type="button" onClick={removeScreenshot} className="absolute top-2 right-2 bg-white/90 text-red-500 p-2 rounded-xl shadow-lg hover:bg-red-50 transition-colors"><FaTimes /></button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="block text-xs font-black text-slate-500 uppercase tracking-widest">Channel Name *</label>
                    <input type="text" name="channelName" value={formData.channelName} onChange={handleInputChange} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-bold" placeholder="e.g. MyChannel" required />
                  </div>
                  <div className="space-y-1">
                    <label className="block text-xs font-black text-slate-500 uppercase tracking-widest">Platform *</label>
                    <select name="platformName" value={formData.platformName} onChange={handleInputChange} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-bold bg-white" required>
                      <option value="">Select</option>
                      {plateforms.map((p, i) => <option key={i} value={p.name || p}>{p.name || p}</option>)}
                    </select>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-black text-slate-500 uppercase tracking-widest">Post Video Link *</label>
                  <input type="url" name="channelLink" value={formData.channelLink} onChange={handleInputChange} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-bold" placeholder="https://..." required />
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-black text-slate-500 uppercase tracking-widest">Ad Timing (MM:SS) *</label>
                  <input type="text" name="adTiming" value={formData.adTiming} onChange={handleInputChange} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-bold" placeholder="00:00" required />
                  <p className="text-[10px] text-slate-400 font-bold">When does the ad appear in the video?</p>
                </div>

                <div className="flex gap-3 pt-4 border-t border-slate-100">
                  <button type="button" onClick={handleCloseModal} className="flex-1 py-4 text-slate-600 bg-slate-50 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-100 transition-all">Cancel</button>
                  <button type="submit" disabled={isSubmitting} className="flex-1 py-4 text-white bg-indigo-600 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-900 shadow-xl shadow-indigo-200 transition-all flex items-center justify-center gap-2">
                    {isSubmitting ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />}
                    {isSubmitting ? "Submitting..." : "Submit Proof"}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      )}

      {!loading && !error && filteredLeads.length === 0 && (
        <div className="max-w-xl mx-auto text-center py-20 px-4">
          <div className="relative inline-block mb-10">
            <div className="absolute inset-0 bg-indigo-500/10 blur-[60px] rounded-full" />
            <div className="relative w-40 h-40 sm:w-48 sm:h-48 rounded-[2.5rem] border-2 border-slate-100 shadow-2xl overflow-hidden mx-auto bg-slate-50 flex items-center justify-center">
              <FaInbox className="text-slate-200 text-6xl sm:text-7xl" />
            </div>
          </div>
          <h3 className="text-slate-900 font-black text-2xl tracking-tight mb-3">No Active Leads</h3>
          <p className="text-slate-500 text-sm font-medium leading-relaxed mb-10 max-w-sm mx-auto">
            You don't have any active advertisements. Accepted ads will appear here for proof submission.
          </p>
          <button onClick={() => navigate('/influencer/advertisement/new')} className="inline-flex items-center gap-3 bg-indigo-600 hover:bg-slate-900 text-white px-10 py-3.5 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] transition-all hover:shadow-2xl hover:shadow-indigo-200">
            Find New Ads
          </button>
        </div>
      )}
    </div>
  );
};

export default InfluenerAcceptedLeads;
